import { Component, inject, OnInit } from '@angular/core';
import { HeaderComponent } from '../../atoms/header/header.component';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import { DataLayerType } from '../../../models/data-layer-type';
import { PoiHelperService } from '../../../services/data-layer-type/poi-helper.service';
import { CheckboxModule } from 'primeng/checkbox';
import { SvgIconComponent } from '../../atoms/svg-icon/svg-icon.component';
import { FormsModule } from '@angular/forms';
import { WidgetStore } from '../../../stores/grid/widget.state';
import {
  CustomGridStackWidget,
  WidgetConfig,
} from '../../../services/grid-widget-config.service';
import { SearchInputComponent } from '../../atoms/search-input/search-input.component';
import { PoiListComponent } from '../../molecules/poi-list/poi-list.component';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { patchState } from '@ngrx/signals';
import { DividerModule } from 'primeng/divider';

@Component({
  selector: 'app-poi-checkboxes',
  standalone: true,
  imports: [
    HeaderComponent,
    CheckboxModule,
    SvgIconComponent,
    FormsModule,
    SearchInputComponent,
    PoiListComponent,
    DividerModule,
  ],
  templateUrl: './poi-checkboxes.component.html',
  styleUrl: './poi-checkboxes.component.scss',
})
export class PoiCheckboxesComponent implements OnInit {
  readonly widgetStore = inject(WidgetStore);
  readonly layerStore = inject(DataLayerStore);

  constructor(private poiHelperService: PoiHelperService) {}

  activeWidget: CustomGridStackWidget | null = null;
  typeSelectItems: DataLayerType[] = [];
  selectedPoiIds: string[] = [];
  config: WidgetConfig | null = null;
  searchWord = '';
  openPoiKey = '';

  ngOnInit(): void {
    this.activeWidget = this.widgetStore.selected();
    this.config = this.activeWidget?.widget || null;
    this.setTypeSelectItems();
    this.initFromConfig();
  }

  /**
   * Updates the typeSelectItems property with the available POI types
   * based on the active year and disabled types.
   *
   * @return {void} Does not return a value.
   */
  setTypeSelectItems(): void {
    this.typeSelectItems = this.poiHelperService.getAvailablePoiTypes(
      this.activeWidget?.widget.year
    );
  }

  /**
   * Initializes the component using the configuration provided.
   * Extracts points of interest (POIs) from the configuration and
   * updates the selectedPoiIds accordingly.
   *
   * @return {void} No return value.
   */
  private initFromConfig(): void {
    if (this.config?.map?.poi) {
      this.selectedPoiIds = Object.keys(this.config.map.poi);
    }
  }

  /**
   * Handles the event triggered when the selection changes.
   * Builds a POI configuration based on the selected items and emits it through the selectionChange event.
   *
   * @return {void} Does not return a value.
   */
  onSelectionChange(): void {
    if (!this.activeWidget?.widget?.map) return;
    const poiConfig = this.poiHelperService.buildPoiConfig(
      this.typeSelectItems.filter(t => this.selectedPoiIds.includes(t._id))
    );
    this.activeWidget.widget.map.poi = Object.keys(poiConfig).length
      ? poiConfig
      : undefined;

    this.widgetStore.updateWidgetConfigById(
      this.activeWidget.id,
      this.activeWidget.widget
    );
  }

  search(searchWord: string): void {
    this.searchWord = searchWord;
  }

  callPoiClickEvent(key: string, typeId: string): void {
    this.openPoiKey = key;
    if (!this.selectedPoiIds.includes(typeId)) {
      this.selectedPoiIds = [...this.selectedPoiIds, typeId];
      this.onSelectionChange();
      setTimeout(() => this.widgetStore.triggerEvent('showPoi', { key }), 300);
    } else {
      queueMicrotask(() => this.widgetStore.triggerEvent('showPoi', { key }));
    }
  }
}
